import { Box, EmptyState, Heading, Image, Input, Loader, Text, TextButton } from '@wix/design-system';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { ChevronRightLarge, RefreshSmall, Search } from '@wix/wix-ui-icons-common/lazy';
import React, { useMemo, useState } from 'react';
import { DomainSearchSuiteModule } from '../../module/domainSearchSuite.module';
import { DATA_HOOKS } from './SiteSelectorPage.driver';
import s from './siteSelectorPage.css';
const QUERY_DISPLAY_LIMIT = 40;
const truncate = value => value.length > QUERY_DISPLAY_LIMIT ? `${value.slice(0, QUERY_DISPLAY_LIMIT)}…` : value;
const SiteRow = _ref => {
  let site = _ref.site,
    onClick = _ref.onClick;
  const _DomainSearchSuiteMod = DomainSearchSuiteModule.internals.useTranslations(),
    t = _DomainSearchSuiteMod.t;
  return /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: onClick,
    className: s.row,
    "data-hook": DATA_HOOKS.siteRow
  }, /*#__PURE__*/React.createElement(Box, {
    width: "102px",
    height: "60px",
    flexShrink: 0,
    borderRadius: "4px",
    overflow: "hidden"
  }, /*#__PURE__*/React.createElement(Image, {
    width: "100%",
    height: "100%",
    src: site.iconUrl,
    fit: "cover",
    transparent: true
  })), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "2px",
    flex: 1,
    minWidth: 0,
    marginLeft: "20px",
    marginRight: "12px"
  }, site.allowedDomain && /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "bold",
    skin: "premium",
    className: s.premiumLabel
  }, t('domains_search_suite.modal.site_selector.list.item.badge.premium')), /*#__PURE__*/React.createElement(Text, {
    size: "medium",
    weight: "thin",
    ellipsis: true,
    showTooltip: false
  }, site.siteName), /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "thin",
    ellipsis: true,
    showTooltip: false
  }, site.published ? site.viewerUrl ?? '' : t('domains_search_suite.modal.site_selector.list.item.status.not_published'))), /*#__PURE__*/React.createElement(Box, {
    flexShrink: 0
  }, /*#__PURE__*/React.createElement(ChevronRightLarge, null)));
};
export const SiteSelectorPage = _ref2 => {
  let domainName = _ref2.domainName,
    onSelectSite = _ref2.onSelectSite,
    requiredPermissions = _ref2.requiredPermissions;
  const _DomainSearchSuiteMod2 = DomainSearchSuiteModule.internals.useTranslations(),
    t = _DomainSearchSuiteMod2.t;
  const _useState = useState(''),
    query = _useState[0],
    setQuery = _useState[1];
  const _DomainsApiClient$hoo = DomainsApiClient.hooks.useDomainsAccount({}),
    data = _DomainsApiClient$hoo.data,
    isLoading = _DomainsApiClient$hoo.isLoading,
    isError = _DomainsApiClient$hoo.isError,
    refetch = _DomainsApiClient$hoo.refetch;
  const filteredSites = useMemo(() => {
    const sites = (data == null ? void 0 : data.sites) ?? [];
    const permissionFilteredSites = requiredPermissions != null && requiredPermissions.length ? sites.filter(site => requiredPermissions.every(permission => {
      var _site$permissions;
      return (_site$permissions = site.permissions) == null ? void 0 : _site$permissions.includes(permission);
    })) : sites;
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return permissionFilteredSites;
    }
    return permissionFilteredSites.filter(site => {
      var _site$siteName;
      return (_site$siteName = site.siteName) == null ? void 0 : _site$siteName.toLowerCase().includes(normalized);
    });
  }, [data == null ? void 0 : data.sites, query, requiredPermissions]);
  return /*#__PURE__*/React.createElement(Box, {
    dataHook: DATA_HOOKS.component,
    direction: "vertical",
    width: "100%",
    height: "100%",
    backgroundColor: "white",
    overflowY: "auto",
    overflowX: "hidden"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    padding: "SP6 SP5"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP2",
    align: "center",
    textAlign: "center",
    paddingBottom: "SP6"
  }, /*#__PURE__*/React.createElement(Heading, {
    size: "large",
    className: s.heading
  }, t('domains_search_suite.gate_mobile.site_selector.title', {
    domainName
  })), /*#__PURE__*/React.createElement(Text, {
    size: "small",
    weight: "thin"
  }, t('domains_search_suite.gate_mobile.site_selector.subtitle'))), /*#__PURE__*/React.createElement(Input, {
    size: "medium",
    border: "round",
    prefix: /*#__PURE__*/React.createElement(Box, {
      paddingLeft: "SP3",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Search, null)),
    placeholder: t('domains_search_suite.gate_mobile.site_selector.search.placeholder'),
    value: query,
    onChange: e => setQuery(e.target.value),
    clearButton: true,
    onClear: () => setQuery('')
  }), isLoading ? /*#__PURE__*/React.createElement(Box, {
    padding: "SP6 0",
    align: "center"
  }, /*#__PURE__*/React.createElement(Loader, {
    size: "medium"
  })) : isError ? /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP3",
    align: "center",
    textAlign: "center",
    padding: "SP6"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    weight: "thin"
  }, t('domain_search_suite.error.technical_issue.full_page.text')), /*#__PURE__*/React.createElement(TextButton, {
    onClick: () => refetch(),
    size: "tiny",
    prefixIcon: /*#__PURE__*/React.createElement(RefreshSmall, null)
  }, t('domain_search_suite.error.technical_issue.full_page.cta'))) : filteredSites.length === 0 ? /*#__PURE__*/React.createElement(Box, {
    padding: "SP6 0",
    align: "center"
  }, /*#__PURE__*/React.createElement(EmptyState, {
    title: query ? t('domains_search_suite.modal.site_selector.list.empty_state.title', {
      searchText: truncate(query)
    }) : t('domains_search_suite.gate_mobile.site_selector.empty_state.title'),
    subtitle: /*#__PURE__*/React.createElement("span", {
      className: s.emptyStateSubtitle
    }, query ? t('domains_search_suite.modal.site_selector.list.empty_state.subtitle') : t('domains_search_suite.gate_mobile.site_selector.empty_state.description'))
  })) : /*#__PURE__*/React.createElement(Box, {
    direction: "vertical"
  }, filteredSites.map(site => /*#__PURE__*/React.createElement(SiteRow, {
    key: site.siteId,
    site: site,
    onClick: () => onSelectSite(site)
  })))));
};
//# sourceMappingURL=SiteSelectorPage.js.map